import React, { useState, useEffect } from 'react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { propertyAPI, unitAPI, leaseAPI, paymentAPI, maintenanceAPI, analyticsAPI } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { Link } from 'react-router-dom';
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import {
  Home,
  DoorOpen,
  Users,
  DollarSign,
  Wrench,
  TrendingUp,
  AlertCircle,
  ArrowRight,
  Calendar,
  CheckCircle,
  Filter,
  Star,
  CreditCard,
  ExternalLink,
} from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { useI18n } from '../../context/I18nContext';

const LandlordDashboard = () => {
  const { user } = useAuth();
  const { t } = useI18n();
  const [stats, setStats] = useState({
    properties: 0,
    units: 0,
    occupiedUnits: 0,
    tenants: 0,
    pendingPayments: 0,
    pendingAmount: 0,
    openTickets: 0
  });
  const [analytics, setAnalytics] = useState(null);
  const [recentPayments, setRecentPayments] = useState([]);
  const [recentTickets, setRecentTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState('all');
  const [filterBuilding, setFilterBuilding] = useState('all');

  useEffect(() => {
    loadDashboardData();
  }, [filterType, filterBuilding]);

  const loadDashboardData = async () => {
    try {
      // Build analytics params with filters
      const analyticsParams = {};
      if (filterType !== 'all') analyticsParams.property_type = filterType;
      if (filterBuilding !== 'all') analyticsParams.building_type = filterBuilding;

      const [propertiesRes, unitsRes, leasesRes, paymentsRes, ticketsRes] = await Promise.all([
        propertyAPI.getAll(analyticsParams),
        unitAPI.getAll(),
        leaseAPI.getAll(),
        paymentAPI.getAll(),
        maintenanceAPI.getAll()
      ]);

      const properties = propertiesRes.data;
      const units = unitsRes.data;
      const leases = leasesRes.data;
      const payments = paymentsRes.data;
      const tickets = ticketsRes.data;

      const occupiedUnits = units.filter(u => u.status === 'OCCUPIED').length;
      const activeLeases = leases.filter(l => l.status === 'ACTIVE').length;
      const pendingPayments = payments.filter(p => p.status === 'PENDING' || p.status === 'OVERDUE');
      const pendingAmount = pendingPayments.reduce((sum, p) => sum + p.amount + (p.late_fee || 0), 0);
      const openTickets = tickets.filter(t => t.status !== 'CLOSED' && t.status !== 'RESOLVED');

      setStats({
        properties: properties.length,
        units: units.length,
        occupiedUnits,
        tenants: activeLeases,
        pendingPayments: pendingPayments.length,
        pendingAmount,
        openTickets: openTickets.length
      });

      setRecentPayments(payments.slice(0, 5));
      setRecentTickets(tickets.slice(0, 5));

      // Load analytics data
      try {
        const analyticsRes = await analyticsAPI.getLandlordAnalytics(analyticsParams);
        setAnalytics(analyticsRes.data);
      } catch (e) {
        console.log('Analytics not available');
      }
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    {
      title: t('dashboard.properties'),
      value: stats.properties,
      icon: Home,
      color: 'text-blue-400',
      link: '/landlord/properties'
    },
    {
      title: t('dashboard.totalUnits'),
      value: stats.units,
      subtitle: `${stats.occupiedUnits} ${t('dashboard.occupied')}`,
      icon: DoorOpen,
      color: 'text-emerald-400',
      link: '/landlord/units'
    },
    {
      title: t('dashboard.activeTenants'),
      value: stats.tenants,
      icon: Users,
      color: 'text-purple-400',
      link: '/landlord/tenants'
    },
    {
      title: t('dashboard.pendingRevenue'),
      value: `$${stats.pendingAmount.toLocaleString()}`,
      subtitle: `${stats.pendingPayments} ${t('dashboard.payments')}`,
      icon: DollarSign,
      color: 'text-gold',
      link: '/landlord/payments'
    },
    {
      title: t('dashboard.openTickets'),
      value: stats.openTickets,
      icon: Wrench,
      color: stats.openTickets > 0 ? 'text-amber-400' : 'text-emerald-400',
      link: '/landlord/maintenance'
    }
  ];

  const getStatusBadge = (status) => {
    const styles = {
      PAID: 'badge-success',
      PENDING: 'badge-warning',
      OVERDUE: 'badge-danger',
      OPEN: 'badge-info',
      IN_PROGRESS: 'badge-warning',
      RESOLVED: 'badge-success',
      CLOSED: 'badge-success'
    };
    return styles[status] || 'badge-info';
  };

  const CHART_COLORS = {
    teal: '#00a99d',
    tealLight: '#00bfb2',
    emerald: '#10b981',
    blue: '#3b82f6',
    purple: '#8b5cf6',
    amber: '#f59e0b'
  };

  const pieColors = [CHART_COLORS.emerald, CHART_COLORS.amber, CHART_COLORS.teal];

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white border border-[#e5e7eb] p-3 rounded-lg shadow-sm">
          <p className="text-sm text-[#1a1a2e] font-medium">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} className="text-sm" style={{ color: entry.color }}>
              {entry.name}: {entry.name.includes('$') || entry.dataKey === 'revenue' 
                ? `$${entry.value.toLocaleString()}` 
                : entry.dataKey === 'occupancy' 
                  ? `${entry.value}%`
                  : entry.value}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <DashboardLayout type="landlord" title={t('dashboard.title')}>
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 lg:gap-6">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-32 bg-card animate-pulse" />
          ))}
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout type="landlord" title={t('dashboard.title')}>
      <div className="space-y-8" data-testid="landlord-dashboard">
        {/* Welcome Section + Filters */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h2 className="font-serif text-xl sm:text-2xl mb-1">
              {t('dashboard.welcome')} <span className="text-gold">{user?.name?.split(' ')[0]}</span>
            </h2>
            <p className="text-sm text-muted-foreground">{t('dashboard.subtitle')}</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-muted-foreground" />
              <Select value={filterType} onValueChange={setFilterType}>
                <SelectTrigger className="w-[140px] h-9 bg-card border-border text-sm" data-testid="filter-property-type">
                  <SelectValue placeholder="Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="residential">Residential</SelectItem>
                  <SelectItem value="commercial">Commercial</SelectItem>
                  <SelectItem value="mixed">Mixed Use</SelectItem>
                </SelectContent>
              </Select>
              <Select value={filterBuilding} onValueChange={setFilterBuilding}>
                <SelectTrigger className="w-[140px] h-9 bg-card border-border text-sm" data-testid="filter-building-type">
                  <SelectValue placeholder="Building" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Buildings</SelectItem>
                  <SelectItem value="apartment">Apartment</SelectItem>
                  <SelectItem value="triplex">Triplex</SelectItem>
                  <SelectItem value="duplex">Duplex</SelectItem>
                  <SelectItem value="retail">Retail</SelectItem>
                  <SelectItem value="warehouse">Warehouse</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button asChild className="bg-gold text-white hover:bg-gold-light">
              <Link to="/landlord/properties" data-testid="add-property-btn">
                Add Property
              </Link>
            </Button>
          </div>
        </div>

        {/* Stripe Connect Setup Banner */}
        {analytics && analytics.connect_status !== 'active' && (
          <Card className="border-gold/30 bg-gold/5" data-testid="connect-setup-banner">
            <CardContent className="p-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 bg-gold/10 flex items-center justify-center rounded-lg shrink-0">
                  <CreditCard className="w-5 h-5 text-gold" />
                </div>
                <div>
                  <p className="font-medium text-sm">Complete your setup to receive rent payments</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Connect your bank account through Stripe to start collecting rent from tenants.
                  </p>
                </div>
              </div>
              <Button asChild size="sm" className="bg-gold text-white hover:bg-gold-light shrink-0">
                <Link to="/landlord/settings" data-testid="connect-stripe-btn">
                  Connect Stripe <ExternalLink className="w-3.5 h-3.5 ml-1.5" />
                </Link>
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 lg:gap-6">
          {statCards.map((stat, index) => (
            <Link key={stat.title} to={stat.link}>
              <Card 
                className="bg-card border-border hover:border-gold/30 transition-all duration-300 cursor-pointer card-animate"
                style={{ animationDelay: `${index * 0.1}s` }}
                data-testid={`stat-card-${stat.title.toLowerCase().replace(' ', '-')}`}
              >
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2">
                        {stat.title}
                      </p>
                      <p className="text-3xl font-semibold">{stat.value}</p>
                      {stat.subtitle && (
                        <p className="text-sm text-muted-foreground mt-1">{stat.subtitle}</p>
                      )}
                    </div>
                    <stat.icon className={`w-6 h-6 ${stat.color}`} />
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>

        {/* Analytics Charts */}
        {analytics && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Revenue Chart */}
            <Card className="bg-card border-border">
              <CardHeader className="border-b border-border">
                <CardTitle className="font-serif text-lg flex items-center gap-2">
                  <DollarSign className="w-5 h-5 text-gold" />
                  {t('dashboard.monthlyRevenue')}
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={analytics.revenue.monthly}>
                      <defs>
                        <linearGradient id="revenueGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor={CHART_COLORS.teal} stopOpacity={0.3}/>
                          <stop offset="95%" stopColor={CHART_COLORS.teal} stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis 
                        dataKey="month" 
                        stroke="#94a3b8" 
                        fontSize={12}
                        tickLine={false}
                      />
                      <YAxis 
                        stroke="#94a3b8" 
                        fontSize={12}
                        tickLine={false}
                        tickFormatter={(value) => `$${value}`}
                      />
                      <Tooltip content={<CustomTooltip />} />
                      <Area
                        type="monotone"
                        dataKey="revenue"
                        stroke={CHART_COLORS.teal}
                        strokeWidth={2}
                        fillOpacity={1}
                        fill="url(#revenueGradient)"
                        name="Revenue"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
                <div className="flex justify-between mt-4 pt-4 border-t border-border">
                  <div>
                    <p className="text-xs uppercase tracking-wider text-muted-foreground">Total Collected</p>
                    <p className="text-xl font-semibold text-emerald-400">${analytics.revenue.total.toLocaleString()}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs uppercase tracking-wider text-muted-foreground">Pending</p>
                    <p className="text-xl font-semibold text-amber-400">${analytics.revenue.pending.toLocaleString()}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Occupancy Chart */}
            <Card className="bg-card border-border">
              <CardHeader className="border-b border-border">
                <CardTitle className="font-serif text-lg flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-emerald-400" />
                  {t('dashboard.occupancyRate')}
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={analytics.occupancy.monthly}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis 
                        dataKey="month" 
                        stroke="#94a3b8" 
                        fontSize={12}
                        tickLine={false}
                      />
                      <YAxis 
                        stroke="#94a3b8" 
                        fontSize={12}
                        tickLine={false}
                        domain={[0, 100]}
                        tickFormatter={(value) => `${value}%`}
                      />
                      <Tooltip content={<CustomTooltip />} />
                      <Bar 
                        dataKey="occupancy" 
                        fill={CHART_COLORS.emerald}
                        radius={[4, 4, 0, 0]}
                        name="Occupancy"
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <div className="flex justify-between mt-4 pt-4 border-t border-border">
                  <div>
                    <p className="text-xs uppercase tracking-wider text-muted-foreground">Current Rate</p>
                    <p className="text-xl font-semibold text-emerald-400">{analytics.occupancy.rate}%</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs uppercase tracking-wider text-muted-foreground">Vacant Units</p>
                    <p className="text-xl font-semibold text-amber-400">{analytics.occupancy.vacant}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Additional Analytics Row */}
        {analytics && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Payment Status Breakdown */}
            <Card className="bg-card border-border">
              <CardHeader className="border-b border-border pb-3">
                <CardTitle className="font-serif text-lg">Payment Status</CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div className="h-48 flex items-center justify-center">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={[
                          { name: 'Paid', value: analytics.payments.paid },
                          { name: 'Pending', value: analytics.payments.pending },
                          { name: 'Overdue', value: analytics.payments.overdue }
                        ]}
                        cx="50%"
                        cy="50%"
                        innerRadius={40}
                        outerRadius={70}
                        paddingAngle={5}
                        dataKey="value"
                      >
                        {pieColors.map((color, index) => (
                          <Cell key={`cell-${index}`} fill={color} />
                        ))}
                      </Pie>
                      <Tooltip content={<CustomTooltip />} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="flex justify-around mt-2">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-emerald-500" />
                    <span className="text-xs">Paid</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-amber-500" />
                    <span className="text-xs">Pending</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-gold" />
                    <span className="text-xs">Overdue</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Maintenance Overview */}
            <Card className="bg-card border-border">
              <CardHeader className="border-b border-border pb-3">
                <CardTitle className="font-serif text-lg">Maintenance</CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-amber-500/10 flex items-center justify-center">
                        <AlertCircle className="w-5 h-5 text-amber-400" />
                      </div>
                      <span>Open Tickets</span>
                    </div>
                    <span className="text-2xl font-semibold text-amber-400">{analytics.maintenance.open}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-emerald-500/10 flex items-center justify-center">
                        <CheckCircle className="w-5 h-5 text-emerald-400" />
                      </div>
                      <span>Resolved</span>
                    </div>
                    <span className="text-2xl font-semibold text-emerald-400">{analytics.maintenance.resolved}</span>
                  </div>
                  <div className="pt-4 border-t border-border">
                    <div className="flex justify-between text-sm text-muted-foreground">
                      <span>Total Tickets</span>
                      <span>{analytics.maintenance.total}</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Quick Metrics + Satisfaction */}
            <Card className="bg-card border-border" data-testid="satisfaction-card">
              <CardHeader className="border-b border-border pb-3">
                <CardTitle className="font-serif text-lg flex items-center gap-2">
                  <Star className="w-5 h-5 text-gold" />
                  Tenant Satisfaction
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div className="space-y-4">
                  {/* Overall Rating */}
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Overall Rating</span>
                    <div className="flex items-center gap-2">
                      {analytics.satisfaction?.overall_avg != null ? (
                        <>
                          <div className="flex items-center gap-0.5">
                            {[1, 2, 3, 4, 5].map(s => (
                              <Star key={s} className={`w-4 h-4 ${s <= Math.round(analytics.satisfaction.overall_avg) ? 'fill-gold text-gold' : 'text-gray-600'}`} />
                            ))}
                          </div>
                          <span className="font-semibold text-gold">{analytics.satisfaction.overall_avg}</span>
                        </>
                      ) : (
                        <span className="text-sm text-muted-foreground italic">No ratings yet</span>
                      )}
                    </div>
                  </div>

                  {/* Survey Count */}
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Total Responses</span>
                    <span className="font-semibold">{analytics.satisfaction?.total_surveys || 0}</span>
                  </div>

                  {/* Per-Property Ratings */}
                  {analytics.satisfaction?.per_property && Object.keys(analytics.satisfaction.per_property).length > 0 && (
                    <div className="pt-4 border-t border-border">
                      <p className="text-xs uppercase tracking-wider text-muted-foreground mb-3">By Property</p>
                      <div className="space-y-2">
                        {analytics.properties?.map(prop => {
                          const rating = analytics.satisfaction.per_property[prop.id];
                          if (!rating) return null;
                          return (
                            <div key={prop.id} className="flex items-center justify-between text-sm">
                              <span className="text-muted-foreground truncate max-w-[120px]">{prop.name}</span>
                              <div className="flex items-center gap-1">
                                <Star className="w-3 h-3 fill-gold text-gold" />
                                <span className="text-gold">{rating}</span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Fallback: General stats if no satisfaction data */}
                  {(!analytics.satisfaction?.overall_avg) && (
                    <div className="pt-4 border-t border-border space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-muted-foreground">Properties</span>
                        <span className="font-semibold">{stats.properties}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-muted-foreground">Total Units</span>
                        <span className="font-semibold">{stats.units}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-muted-foreground">Active Leases</span>
                        <span className="font-semibold">{stats.tenants}</span>
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Two Column Layout - Recent Activity */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Recent Payments */}
          <Card className="bg-card border-border">
            <CardHeader className="flex flex-row items-center justify-between border-b border-border">
              <CardTitle className="font-serif text-lg">Recent Payments</CardTitle>
              <Link to="/landlord/payments" className="text-gold text-sm flex items-center gap-1 hover:underline">
                {t('dashboard.viewAll')} <ArrowRight className="w-4 h-4" />
              </Link>
            </CardHeader>
            <CardContent className="p-0">
              {recentPayments.length === 0 ? (
                <div className="p-6 text-center text-muted-foreground">
                  <DollarSign className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No payments yet</p>
                </div>
              ) : (
                <div className="divide-y divide-border">
                  {recentPayments.map((payment) => (
                    <div key={payment.id} className="px-6 py-4 flex items-center justify-between" data-testid={`payment-${payment.id}`}>
                      <div>
                        <p className="font-medium">${payment.amount.toLocaleString()}</p>
                        <p className="text-sm text-muted-foreground">Due: {payment.due_date}</p>
                      </div>
                      <span className={`px-3 py-1 text-xs uppercase tracking-wider ${getStatusBadge(payment.status)}`}>
                        {payment.status}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Recent Maintenance Tickets */}
          <Card className="bg-card border-border">
            <CardHeader className="flex flex-row items-center justify-between border-b border-border">
              <CardTitle className="font-serif text-lg">Maintenance Tickets</CardTitle>
              <Link to="/landlord/maintenance" className="text-gold text-sm flex items-center gap-1 hover:underline">
                {t('dashboard.viewAll')} <ArrowRight className="w-4 h-4" />
              </Link>
            </CardHeader>
            <CardContent className="p-0">
              {recentTickets.length === 0 ? (
                <div className="p-6 text-center text-muted-foreground">
                  <Wrench className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No maintenance tickets</p>
                </div>
              ) : (
                <div className="divide-y divide-border">
                  {recentTickets.map((ticket) => (
                    <div key={ticket.id} className="px-6 py-4 flex items-center justify-between" data-testid={`ticket-${ticket.id}`}>
                      <div>
                        <p className="font-medium">{ticket.title}</p>
                        <p className="text-sm text-muted-foreground">Priority: {ticket.priority}</p>
                      </div>
                      <span className={`px-3 py-1 text-xs uppercase tracking-wider ${getStatusBadge(ticket.status)}`}>
                        {ticket.status}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default LandlordDashboard;
